// T-324 D — Aggregazione righe import.
// Quando un DDT/fattura riporta lo STESSO articolo, stesso lotto e stessa
// scadenza su più righe (caso tipico: riga normale + riga "sconto merce/omaggio"
// dello stesso prodotto), le fondiamo in UNA riga sommando le quantità: il
// ristoratore manda il documento e clicca OK senza ripulire i doppioni.
//
// T-481: l'identità primaria è il codice articolo fornitore, quando presente;
// la descrizione è solo il fallback. UdM sorgente, lotto e scadenza restano
// parte della coordinata: quantità non comparabili non vengono mai sommate.

import { normalizeUom } from '../uom.js';
// Solo i campi che leggiamo, tutti opzionali → QUALSIASI oggetto `parsed`
// (incluse interfacce tipizzate come DryRunLine.parsed, che NON sono assegnabili
// a Record<string,unknown> per via dell'index signature mancante) soddisfa il
// vincolo, così il tipo concreto T si preserva al chiamante.
export interface AggParsed {
  supplier_code?: string | null;
  description?: string | null;
  source_uom?: string | null;
  uom?: string | null;
  uom_canonical?: string | null;
  qty?: number | null;
  total_weight_kg?: number | null;
  unit_price?: number | null;
  line_total?: number | null;
  batch_lot?: string | null;
  expiration_date?: string | null;
  is_gift?: boolean | null;
  /**
   * Evidenza delle componenti economiche fuse. Serve a distinguere un valore
   * determinato da un fallback inventato e resta disponibile al debug/review.
   */
  aggregation_evidence?: Array<{
    qty: number | null;
    unit_price: number | null;
    line_total: number | null;
    effective_total: number | null;
  }>;
  aggregation_value_complete?: boolean;
}

export interface HasParsed {
  parsed: AggParsed;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const norm = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');
const finite = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

function effectiveLineTotal(parsed: AggParsed): number | null {
  const qty = finite(parsed.qty);
  if (qty == null || qty <= 0) return null;
  const explicit = finite(parsed.line_total);
  if (explicit != null) return explicit >= 0 ? explicit : null;
  const unitPrice = finite(parsed.unit_price);
  return unitPrice != null && unitPrice >= 0
    ? qty * unitPrice
    : null;
}

function stableEconomicNumber(value: number): number {
  return Number(value.toPrecision(15));
}

function effectiveWeightKg(parsed: AggParsed): number | null {
  const explicit = finite(parsed.total_weight_kg);
  if (explicit != null && explicit > 0) return explicit;
  const qty = finite(parsed.qty);
  if (qty == null || qty <= 0) return null;
  const sourceUom = normalizeUom(
    parsed.source_uom ?? parsed.uom ?? parsed.uom_canonical ?? '',
  );
  if (sourceUom === 'kg') return qty;
  if (sourceUom === 'g') return qty / 1000;
  return null;
}

/**
 * Chiave di aggregazione: identità articolo (solo codice fornitore se presente,
 * altrimenti descrizione normalizzata) + UdM sorgente comparabile + lotto +
 * scadenza. Il valore raw della UdM non viene modificato.
 */
export function aggregationKey(p: AggParsed): string {
  const code = norm(str(p.supplier_code));
  const description = norm(str(p.description));
  const id = code
    ? `c:${code}`
    : `d:${description}`;
  const sourceUom = p.source_uom ?? p.uom ?? p.uom_canonical ?? '';
  return [
    id,
    `u:${normalizeUom(sourceUom)}`,
    `l:${norm(str(p.batch_lot))}`,
    `e:${norm(str(p.expiration_date))}`,
  ].join('|');
}

/**
 * Fonde le righe con la stessa `aggregationKey`. Il valore viene determinato
 * PER COMPONENTE (`line_total`, oppure `unit_price × qty`) prima della somma:
 * così una riga omaggio o un prezzo differente non eredita il prezzo della
 * prima riga. `unit_price` aggregato è la media ponderata valore/qty.
 *
 * Se anche una sola componente non ha valore determinabile, `line_total` e
 * `unit_price` aggregati diventano null (fail-closed) e l'evidenza resta in
 * `aggregation_evidence`. Conserva l'ordine e gli altri campi della prima
 * occorrenza; non muta l'input.
 */
export function aggregateExtractedLines<T extends HasParsed>(lines: T[]): T[] {
  const order: string[] = [];
  const byKey = new Map<string, {
    line: T;
    qty: number;
    total: number;
    quantityComplete: boolean;
    weightKg: number;
    weightComplete: boolean;
    valueComplete: boolean;
    evidence: NonNullable<AggParsed['aggregation_evidence']>;
  }>();
  for (const [lineIndex, line] of lines.entries()) {
    const lineQty = finite(line.parsed.qty);
    const quantityValid = lineQty != null && lineQty > 0;
    // Una quantità invalida non deve sparire dentro una riga aggregata valida.
    // Resta una card separata, con il valore originale, così i gate di review
    // obbligano l'operatore a correggerla o escluderla esplicitamente.
    const key = quantityValid
      ? aggregationKey(line.parsed)
      : `${aggregationKey(line.parsed)}|invalid-qty:${lineIndex}`;
    const existing = byKey.get(key);
    const componentQty = quantityValid ? lineQty! : 0;
    const effectiveTotal = effectiveLineTotal(line.parsed);
    const effectiveWeight = effectiveWeightKg(line.parsed);
    const evidence = {
      qty: lineQty,
      unit_price: finite(line.parsed.unit_price),
      line_total: finite(line.parsed.line_total),
      effective_total: effectiveTotal,
    };
    const componentComplete = quantityValid && effectiveTotal != null;
    if (!existing) {
      const copy = { ...line, parsed: { ...line.parsed } } as T;
      byKey.set(key, {
        line: copy,
        qty: componentQty,
        total: effectiveTotal ?? 0,
        quantityComplete: quantityValid,
        weightKg: effectiveWeight ?? 0,
        weightComplete: effectiveWeight != null,
        valueComplete: componentComplete,
        evidence: [evidence],
      });
      order.push(key);
      continue;
    }
    const ep = existing.line.parsed;
    const lp = line.parsed;
    existing.qty += componentQty;
    existing.total += effectiveTotal ?? 0;
    existing.quantityComplete &&= quantityValid;
    existing.weightKg += effectiveWeight ?? 0;
    existing.weightComplete &&= effectiveWeight != null;
    existing.valueComplete &&= componentComplete;
    existing.evidence.push(evidence);
    ep.is_gift = Boolean(ep.is_gift) && Boolean(lp.is_gift);
  }
  return order.map((key) => {
    const aggregated = byKey.get(key)!;
    const parsed = aggregated.line.parsed;
    parsed.aggregation_evidence = aggregated.evidence;
    parsed.aggregation_value_complete = aggregated.valueComplete;
    if (aggregated.quantityComplete) {
      parsed.qty = stableEconomicNumber(aggregated.qty);
    }
    // CapturePhotoPage usa total_weight_kg per precompilare la quantità di
    // magazzino. Dopo una fusione deve quindi ricevere il totale di TUTTE le
    // componenti, mai il peso parziale rimasto sulla prima riga.
    if (aggregated.evidence.length > 1) {
      parsed.total_weight_kg = aggregated.weightComplete
        ? stableEconomicNumber(aggregated.weightKg)
        : null;
    }
    if (aggregated.valueComplete && aggregated.qty > 0) {
      parsed.line_total = stableEconomicNumber(aggregated.total);
      parsed.unit_price = stableEconomicNumber(
        aggregated.total / aggregated.qty,
      );
    } else {
      parsed.line_total = null;
      parsed.unit_price = null;
    }
    return aggregated.line;
  });
}
